import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, FileText, Image as ImageIcon, AlertCircle, Loader2, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export const FileUploadZone = ({ orderId, assets = [], onUploadComplete, onDelete }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const validate = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) return `"${file.name}" is not allowed. Only JPEG, PNG, and PDF accepted.`;
    if (file.size > MAX_SIZE) return `"${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 5MB.`;
    return null;
  };

  const uploadFiles = useCallback(async (files) => {
    setError('');
    const validFiles = [];
    for (const f of files) {
      const err = validate(f);
      if (err) { setError(err); return; }
      validFiles.push(f);
    }
    setUploading(true);
    try {
      for (const file of validFiles) {
        const form = new FormData();
        form.append('file', file);
        form.append('order_id', orderId);
        await axios.post(`${API}/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, onUploadComplete]);

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); uploadFiles(Array.from(e.dataTransfer.files)); };
  const handleInput = (e) => { if (e.target.files?.length) uploadFiles(Array.from(e.target.files)); };

  const isImage = (type) => type?.startsWith('image/');

  return (
    <div data-testid="file-upload-zone">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
          dragOver ? 'border-brand-blue bg-brand-blue/5' : 'border-slate-200 hover:border-brand-blue/30'
        }`}
        onClick={() => document.getElementById(`file-input-${orderId}`)?.click()}
      >
        <input id={`file-input-${orderId}`} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={handleInput} data-testid="file-input" />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-brand-blue"><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm font-medium">Uploading...</span></div>
        ) : (
          <>
            <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Drag & drop files here, or click to browse</p>
            <p className="text-[10px] text-slate-400 mt-1">JPEG, PNG, PDF &middot; Max 5MB each</p>
          </>
        )}
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-2">
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-50 border border-red-100">
              <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
              <p className="text-[11px] text-red-600">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Asset list */}
      {assets.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="asset-list">
          {assets.map((asset) => (
            <div key={asset.id} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
              {isImage(asset.type) ? (
                <img src={`${BACKEND_URL}${asset.url}`} alt={asset.filename} className="w-8 h-8 rounded object-cover border border-slate-200" />
              ) : (
                <div className="w-8 h-8 rounded bg-red-50 flex items-center justify-center"><FileText className="w-4 h-4 text-red-500" /></div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">{asset.filename}</p>
                <p className="text-[10px] text-slate-400">{(asset.size / 1024).toFixed(0)}KB</p>
              </div>
              <a href={`${BACKEND_URL}${asset.url}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-brand-blue font-semibold hover:underline">View</a>
              {onDelete && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(asset.id); }} className="w-6 h-6 rounded flex items-center justify-center hover:bg-red-50 transition-colors" data-testid={`delete-asset-${asset.id}`}>
                  <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
